class SearchForm extends HTMLElement {
	constructor() {
		super();
		this.input = this.querySelector('input[type="search"]');

		if (this.input) {
			this.input.form.addEventListener("reset", this.onFormReset.bind(this));
			this.input.addEventListener(
				"input",
				debounce((event) => {
					this.onChange(event);
				}, 300).bind(this),
			);
		}
	}

	shouldResetForm() {
		return !document.querySelector('[aria-selected="true"] a');
	}

	onFormReset(event) {
		// Prevent default so the form reset doesn't set the value gotten from the url on page load
		event.preventDefault();
		// Don't reset if the user has selected an element on the predictive search dropdown
		if (this.shouldResetForm()) {
			this.input.value = "";
			this.input.focus();
		}
	}
}

customElements.define("search-form", SearchForm);


// Keep catalogue search focused on names, product types, brands and real variants.
// Description copy is deliberately excluded (for example, "double-sided weaving").
(() => {
  const words = (value) => String(value || '').normalize('NFKD').toLowerCase()
    .replace(/[\u0300-\u036f]/g, '').match(/[\p{L}\p{N}]+/gu) || [];
  const singular = (word) => word.endsWith('sses') ? word.slice(0, -2)
    : word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word;
  const aliases = { couch: 'sofa', couches: 'sofa' };
  const canonical = (word) => singular(aliases[word] || word);
  const advanced = (query) => /[():]|\b(?:AND|OR|NOT)\b/.test(query);
  function near(a, b) {
    if (a.length < 4 || Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, errors = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++errors > 1) return false;
      if (a.length === b.length && a[i] === b[j + 1] && a[i + 1] === b[j]) { i += 2; j += 2; }
      else if (a.length > b.length) i++;
      else if (b.length > a.length) j++;
      else { i++; j++; }
    }
    return errors + (a.length - i) + (b.length - j) <= 1;
  }
  function matches(fields, query, fuzzy = false) {
    if (advanced(query)) return true;
    const terms = words(query).map(canonical);
    const catalogue = words(fields).map(canonical);
    return terms.every((term, index) => catalogue.some((word) => word === term
      || (index === terms.length - 1 && word.startsWith(term))
      || (fuzzy && near(term, word))));
  }
  function relevant(items, query) {
    const exact = items.filter((item) => matches(item.dataset.woodtalkFields, query));
    return exact.length ? exact : items.filter((item) => matches(item.dataset.woodtalkFields, query, true));
  }
  function filterMarkup(markup, query) {
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const items = Array.from(parsed.querySelectorAll('[data-woodtalk-fields]'));
    const keep = new Set(relevant(items, query));
    items.forEach((item) => { if (!keep.has(item)) item.remove(); });
    parsed.querySelectorAll('.predictive-search__results').forEach((group) => {
      if (!group.querySelector('[role="option"]')) group.remove();
    });
    const count = parsed.querySelectorAll('[role="option"]').length;
    const status = parsed.querySelector('[data-predictive-search-live-region-count-value]');
    if (status) status.textContent = `${count} matching results`;
    // Always give customers a route to the complete, filtered result set.
    const resultBox = parsed.querySelector('#predictive-search-results');
    if (resultBox) {
      const link = parsed.createElement('a');
      const url = new URL(routes.search_url || '/search', location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      link.href = url.pathname + url.search;
      link.className = 'predictive-search__text link';
      link.textContent = `View all results for “${query}”`;
      resultBox.append(link);
    }
    return parsed.body.innerHTML;
  }
  const pageSize = 24;
  async function initialize(root) {
    const marker = root.querySelector('[data-woodtalk-source]');
    if (!marker || root.dataset.woodtalkReady) return;
    root.dataset.woodtalkReady = 'loading';
    root.setAttribute('aria-busy', 'true');
    const grid = root.querySelector('#product-grid');
    if (!grid) { root.removeAttribute('aria-busy'); return; }
    const overlay = grid.querySelector('.loading-overlay');
    if (overlay) overlay.classList.remove('hidden');
    const sourcePage = Number(marker.dataset.currentPage);
    const sourcePages = Number(marker.dataset.sourcePages);
    const documents = new Map([[sourcePage, root]]);
    const query = root.dataset.woodtalkQuery;
    try {
      // Read every native page before computing counts or pagination, so later
      // matches are retained even when the first native page has mostly noise.
      const pending = Array.from({ length: sourcePages }, (_, i) => i + 1)
        .filter((page) => page !== sourcePage);
      while (pending.length) {
        await Promise.all(pending.splice(0, 3).map(async (page) => {
          const url = new URL(location.href);
          url.searchParams.set('page', page);
          url.searchParams.set('section_id', marker.dataset.sectionId);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Search page unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = parsed.querySelector('[data-woodtalk-search]');
          if (!section?.querySelector('[data-woodtalk-source]')) throw new Error('Search page incomplete');
          documents.set(page, section);
        }));
      }
      const seen = new Set();
      const candidates = Array.from(documents).sort((a, b) => a[0] - b[0])
        .flatMap(([, section]) => Array.from(section.querySelectorAll('[data-woodtalk-fields]')))
        .filter((item) => {
          const key = item.dataset.woodtalkKey;
          if (seen.has(key)) return false;
          seen.add(key); return true;
        });
      const results = relevant(candidates, query);
      const pageCount = Math.ceil(results.length / pageSize);
      let currentPage = Math.min(Math.max(1, Number(new URL(location.href).searchParams.get('page')) || 1), pageCount || 1);
      const groups = ['products', 'articles', 'pages'];
      const groupTemplates = new Map();
      for (const [, section] of documents) {
        for (const type of groups) {
          const list = section.querySelector(`.collection-results--${type}`);
          if (list && !groupTemplates.has(type)) groupTemplates.set(type, {
            list: list.cloneNode(false), title: list.previousElementSibling?.cloneNode(true)
          });
        }
      }
      grid.querySelectorAll('.collection-results, .search-results__title, .pagination-wrapper, .title-wrapper-empty').forEach((node) => node.remove());
      const visible = results.slice((currentPage - 1) * pageSize, currentPage * pageSize);
      groups.forEach((type) => {
        const entries = visible.filter((item) => item.dataset.woodtalkKind === type);
        const template = groupTemplates.get(type);
        if (!entries.length || !template) return;
        const title = template.title;
        if (title?.classList.contains('search-results__title')) grid.append(title.cloneNode(true));
        const list = template.list.cloneNode(false);
        entries.forEach((item) => list.append(item.cloneNode(true)));
        grid.append(list);
      });
      const label = results.length === 1 ? root.dataset.woodtalkCountOne
        : root.dataset.woodtalkCountMany.replace('987654321', String(results.length));
      const status = root.querySelector('.template-search__header-wrapper > [role="status"]');
      if (status) status.textContent = label;
      document.title = `Search: ${label}`;
      const breadcrumb = document.querySelector('nav[aria-label="breadcrumbs"] > div:last-child');
      if (breadcrumb) breadcrumb.textContent = `Search: ${label}`;
      if (!results.length) {
        const empty = document.createElement('p');
        empty.className = 'search__form-text search__form-no-result';
        empty.textContent = root.dataset.woodtalkEmptyLabel;
        grid.append(empty);
      }
      if (pageCount > 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'pagination-wrapper';
        const nav = document.createElement('nav');
        nav.className = 'pagination'; nav.setAttribute('aria-label', 'Pagination');
        const list = document.createElement('ul'); list.className = 'pagination__list list-unstyled';
        const pages = new Set([1, currentPage - 1, currentPage, currentPage + 1, pageCount]);
        Array.from(pages).filter((page) => page > 0 && page <= pageCount).sort((a, b) => a - b).forEach((page) => {
          const item = document.createElement('li');
          const link = document.createElement(page === currentPage ? 'span' : 'a');
          link.className = 'pagination__item' + (page === currentPage ? ' pagination__item--current' : ' link');
          link.textContent = page; link.setAttribute('aria-label', `Page ${page}`);
          if (page === currentPage) link.setAttribute('aria-current', 'page');
          else { const url = new URL(location.href); url.searchParams.set('page', page); link.href = url.pathname + url.search; }
          item.append(link); list.append(item);
        });
        nav.append(list); wrapper.append(nav); grid.append(wrapper);
      }
      const url = new URL(location.href);
      if (Number(url.searchParams.get('page') || 1) !== currentPage) {
        if (currentPage === 1) url.searchParams.delete('page'); else url.searchParams.set('page', currentPage);
        history.replaceState(null, '', url.pathname + url.search);
      }
      root.dataset.woodtalkReady = 'complete';
      if (typeof colorSwatches === 'function') colorSwatches();
    } catch (error) {
      // Retain the complete native grid and its pagination if a page cannot load.
      root.dataset.woodtalkReady = 'fallback';
      console.warn('Wood Talk search used native results because a page could not load.');
    } finally {
      root.setAttribute('aria-busy', 'false');
      if (overlay) overlay.classList.add('hidden');
    }
  }
  window.WoodTalkSearch = { matches, filterMarkup, initialize };
  function start() { document.querySelectorAll('[data-woodtalk-search]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
